import React, { useState } from "react";
import {
  Card,
  Button,
  Select,
  DatePicker,
  Space,
  Row,
  Col,
  Typography,
  Popconfirm,
  message,
  Switch,
  Divider,
  Tag,
  Timeline,
  Form,
  Modal,
  Input,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StarOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { WorkflowAssignment, User, ModificationHistory } from "../../../types";
import { formatDate, formatDateTime } from "../../../utils/dateUtils";
import dayjs from "dayjs";
import styled from "styled-components";
import TextArea from "antd/es/input/TextArea";

const { Option } = Select;
const { Text, Title } = Typography;

const AssignmentCard = styled(Card)`
  margin-bottom: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &.primary {
    border-left: 4px solid #1890ff;
  }

  &.completed {
    background: #f6ffed;
    border-color: #52c41a;
  }

  &.in-progress {
    background: #fff7e6;
    border-color: #faad14;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 16px;

    .icon {
      margin-right: 8px;
    }
  }
`;

// Add these new props to WorkflowAssignmentsProps interface:
interface WorkflowAssignmentsProps {
  assignments: WorkflowAssignment[];
  users: User[];
  role: "visiteur" | "elaborateur" | "validateur";
  title: string;
  icon: React.ReactNode;
  canAdd: boolean;
  canAssignValidators?: boolean;
  onAddAssignment: (assignment: Partial<WorkflowAssignment>) => void;
  onUpdateAssignment: (
    assignmentId: number,
    data: Partial<WorkflowAssignment>
  ) => void;
  onRemoveAssignment: (assignmentId: number) => void;
  // NOUVEAU :
  onAddModification?: (assignmentId: number, observations: string) => void;
  onStartModification?: (modificationId: number, dateStarted: string) => void; // NOUVEAU
  onCompleteModification?: (
    modificationId: number,
    dateCompleted: string,
    observations?: string
  ) => void;
}

const WorkflowAssignments: React.FC<WorkflowAssignmentsProps> = ({
  assignments,
  users,
  role,
  title,
  icon,
  canAdd,
  canAssignValidators = true,
  onAddAssignment,
  onUpdateAssignment,
  onRemoveAssignment,
  onAddModification,
  onCompleteModification,
  onStartModification
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAssignment, setNewAssignment] = useState<
    Partial<WorkflowAssignment>
  >({
    role,
    is_primary: false,
  });

  const handleAddAssignment = () => {
    if (!newAssignment.user) {
      message.error("Veuillez sélectionner un utilisateur");
      return;
    }

    onAddAssignment(newAssignment);
    setNewAssignment({ role, is_primary: false });
    setShowAddForm(false);
  };

  const handleUpdateAssignment = (
    assignmentId: number,
    field: string,
    value: any
  ) => {
    onUpdateAssignment(assignmentId, { [field]: value });
  };

  const getAssignmentStatus = (assignment: WorkflowAssignment) => {
    if (assignment.date_completed) return "completed";
    if (assignment.date_started) return "in-progress";
    return "pending";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "in-progress":
        return <ClockCircleOutlined style={{ color: "#faad14" }} />;
      default:
        return <UserOutlined style={{ color: "#d9d9d9" }} />;
    }
  };

  const getAvailableUsers = () => {
    const assignedUserIds = assignments.map((a) => a.user);
    return users.filter(
      (u) => u.role === role && u.is_active && !assignedUserIds.includes(u.id)
    );
  };

  const canShowAddButton =
    canAdd && (role !== "validateur" || canAssignValidators);

  return (
    <div>
      <SectionHeader>
        <div className="title">
          <span className="icon">{icon}</span>
          {title}
          {assignments.length > 0 && (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              ({assignments.length})
            </Text>
          )}
        </div>
        {canShowAddButton && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setShowAddForm(true)}
            size="small"
          >
            Ajouter
          </Button>
        )}
      </SectionHeader>

      {role === "validateur" && !canAssignValidators && (
        <Card
          size="small"
          style={{
            marginBottom: 16,
            background: "#fff7e6",
            borderColor: "#faad14",
          }}
        >
          <Text type="secondary">
            <ClockCircleOutlined style={{ marginRight: 8 }} />
            Les validateurs ne peuvent être assignés qu'après qu'au moins un
            élaborateur ait terminé son travail.
          </Text>
        </Card>
      )}

      {assignments.map((assignment) => {
        const status = getAssignmentStatus(assignment);
        return (
          <AssignmentCard
            key={assignment.id}
            size="small"
            className={`${assignment.is_primary ? "primary" : ""} ${status}`}
          >
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  {getStatusIcon(status)}
                  <Text strong style={{ marginLeft: 8 }}>
                    {assignment.user_name}
                  </Text>
                  {assignment.is_primary && (
                    <StarOutlined style={{ color: "#faad14", marginLeft: 8 }} />
                  )}
                  {assignment.user_is_active === false && (
                    <Tag
                      color="orange"
                      style={{ marginLeft: 8, fontSize: "10px" }}
                    >
                      Inactif
                    </Tag>
                  )}
                  <Text
                    type="secondary"
                    style={{ marginLeft: 8, fontSize: "12px" }}
                  >
                    {assignment.user_email}
                  </Text>
                </div>

                <Row gutter={[8, 8]}>
                  <Col span={6}>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Principal:
                    </Text>
                    <br />
                    <Switch
                      size="small"
                      checked={assignment.is_primary}
                      onChange={(checked) =>
                        handleUpdateAssignment(
                          assignment.id!,
                          "is_primary",
                          checked
                        )
                      }
                    />
                  </Col>

                  {role === "validateur" && (
                    <Col span={6}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Prévalidation:
                      </Text>
                      <br />
                      <DatePicker
                        size="small"
                        value={
                          assignment.date_prevalidation
                            ? dayjs(assignment.date_prevalidation)
                            : null
                        }
                        onChange={(date) =>
                          handleUpdateAssignment(
                            assignment.id!,
                            "date_prevalidation",
                            date ? date.format("YYYY-MM-DD") : null
                          )
                        }
                        format="DD/MM/YYYY"
                        style={{ width: "100%" }}
                      />
                    </Col>
                  )}

                  <Col span={6}>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Date début:
                    </Text>
                    <br />
                    <DatePicker
                      size="small"
                      value={
                        assignment.date_started
                          ? dayjs(assignment.date_started)
                          : null
                      }
                      onChange={(date) =>
                        handleUpdateAssignment(
                          assignment.id!,
                          "date_started",
                          date ? date.format("YYYY-MM-DD") : null
                        )
                      }
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                    />
                  </Col>

                  <Col span={6}>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Date fin:
                    </Text>
                    <br />
                    <DatePicker
                      size="small"
                      value={
                        assignment.date_completed
                          ? dayjs(assignment.date_completed)
                          : null
                      }
                      onChange={(date) =>
                        handleUpdateAssignment(
                          assignment.id!,
                          "date_completed",
                          date ? date.format("YYYY-MM-DD") : null
                        )
                      }
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                    />
                  </Col>
                </Row>

                {/* ADD THE MODIFICATION HISTORY SECTION HERE - ONLY FOR ELABORATEURS */}
                {role === "elaborateur" &&
                  onAddModification &&
                  onCompleteModification && (
                    <ModificationHistorySection
                      assignment={assignment}
                      onAddModification={onAddModification}
                      onStartModification={onStartModification} // NOUVEAU
                      onCompleteModification={onCompleteModification}
                      canManage={true}
                    />
                  )}
              </Col>

              <Col>
                <Popconfirm
                  title="Supprimer cette assignation?"
                  onConfirm={() => onRemoveAssignment(assignment.id!)}
                  okText="Oui"
                  cancelText="Non"
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                  />
                </Popconfirm>
              </Col>
            </Row>
          </AssignmentCard>
        );
      })}

      {showAddForm && (
        <Card
          size="small"
          style={{ marginBottom: 16, border: "2px dashed #1890ff" }}
        >
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Utilisateur:
              </Text>
              <Select
                style={{ width: "100%" }}
                placeholder="Sélectionner un utilisateur"
                value={newAssignment.user}
                onChange={(value) =>
                  setNewAssignment({ ...newAssignment, user: value })
                }
                showSearch
                optionFilterProp="children"
              >
                {getAvailableUsers().map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col span={4}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Principal:
              </Text>
              <br />
              <Switch
                size="small"
                checked={newAssignment.is_primary}
                onChange={(checked) =>
                  setNewAssignment({ ...newAssignment, is_primary: checked })
                }
              />
            </Col>

            <Col span={8}>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleAddAssignment}
                  disabled={!newAssignment.user}
                >
                  Ajouter
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewAssignment({ role, is_primary: false });
                  }}
                >
                  Annuler
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {assignments.length === 0 && !showAddForm && (
        <Card
          size="small"
          style={{ textAlign: "center", background: "#fafafa" }}
        >
          <Text type="secondary">Aucun {role} assigné</Text>
        </Card>
      )}
    </div>
  );
};

export default WorkflowAssignments;

const ModificationHistorySection: React.FC<{
  assignment: WorkflowAssignment;
  onAddModification: (assignmentId: number, observations: string) => void;
  onCompleteModification: (modificationId: number, dateCompleted: string, observations?: string) => void;
  onStartModification?: (modificationId: number, dateStarted: string) => void;
  canManage: boolean;
}> = ({ assignment, onAddModification, onCompleteModification, onStartModification, canManage }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedModification, setSelectedModification] = useState<ModificationHistory | null>(null);
  const [addForm] = Form.useForm();
  const [completeForm] = Form.useForm();

  const handleAddModification = (values: any) => {
    onAddModification(assignment.id!, values.observations);
    setShowAddModal(false);
    addForm.resetFields();
  };

  const handleCompleteModification = (values: any) => {
    if (selectedModification) {
      onCompleteModification(
        selectedModification.id,
        values.date_completed.format('YYYY-MM-DD'),
        values.observations
      );
      setShowCompleteModal(false);
      setSelectedModification(null);
      completeForm.resetFields();
    }
  };

  const handleStartModification = async (modificationId: number) => {
    if (onStartModification) {
      onStartModification(modificationId, dayjs().format('YYYY-MM-DD'));
    }
  };

  if (!assignment.modification_history?.length && !canManage) return null;

  return (
    <div style={{ marginTop: 12, padding: 12, background: '#fff7e6', borderRadius: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Text strong style={{ fontSize: '12px', color: '#d46b08' }}>
          <EditOutlined style={{ marginRight: 4 }} />
          Historique des modifications
        </Text>
        {canManage && (
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setShowAddModal(true)}
          >
            Demander modification
          </Button>
        )}
      </div>

      {assignment.modification_history && assignment.modification_history.length > 0 ? (
        <Timeline>
          {assignment.modification_history.map((mod) => (
            <Timeline.Item
              key={mod.id}
              dot={
                mod.is_completed ? (
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                ) : mod.date_started ? (
                  <ClockCircleOutlined style={{ color: '#faad14' }} />
                ) : (
                  <UserOutlined style={{ color: '#d9d9d9' }} />
                )
              }
            >
              <div style={{ fontSize: '11px' }}>
                <div style={{ fontWeight: 500 }}>
                  {mod.is_completed 
                    ? 'Modification terminée' 
                    : mod.date_started 
                    ? 'Modification en cours'
                    : 'Modification demandée'
                  }
                </div>
                
                <div style={{ color: '#666', marginTop: 2 }}>
                  Demandée: {formatDateTime(mod.date_requested)}
                </div>
                
                {mod.date_started && (
                  <div style={{ color: '#1890ff', marginTop: 2 }}>
                    Commencée: {formatDate(mod.date_started)}
                  </div>
                )}
                
                {mod.date_completed && (
                  <div style={{ color: '#52c41a', marginTop: 2 }}>
                    Terminée: {formatDate(mod.date_completed)}
                  </div>
                )}
                
                {mod.observations && (
                  <div style={{ 
                    marginTop: 4, 
                    padding: 6, 
                    background: '#f5f5f5', 
                    borderRadius: 4,
                    fontStyle: 'italic'
                  }}>
                    "{mod.observations}"
                  </div>
                )}
                
                {/* BOUTON COMMENCER - Affiché si modification pas commencée */}
                {!mod.is_completed && !mod.date_started && canManage && (
                  <Button
                    size="small"
                    type="link"
                    onClick={() => handleStartModification(mod.id)}
                    style={{ padding: 0, height: 'auto', marginTop: 4 }}
                  >
                    Commencer la modification
                  </Button>
                )}
                
                {/* BOUTON TERMINER - Affiché si modification commencée mais pas terminée */}
                {!mod.is_completed && mod.date_started && canManage && (
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      setSelectedModification(mod);
                      setShowCompleteModal(true);
                      completeForm.setFieldsValue({ 
                        observations: mod.observations,
                        date_completed: dayjs()
                      });
                    }}
                    style={{ padding: 0, height: 'auto', marginTop: 4 }}
                  >
                    Marquer comme terminé
                  </Button>
                )}
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      ) : (
        <Text type="secondary" style={{ fontSize: '11px' }}>
          Aucune modification demandée
        </Text>
      )}

      {/* Modal pour ajouter une demande de modification */}
      <Modal
        title="Demander une modification"
        open={showAddModal}
        onCancel={() => {
          setShowAddModal(false);
          addForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={addForm} onFinish={handleAddModification} layout="vertical">
          <Form.Item
            name="observations"
            label="Observations/Instructions"
            rules={[{ required: true, message: 'Veuillez saisir les observations' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Décrivez les modifications nécessaires..."
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setShowAddModal(false);
                addForm.resetFields();
              }}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit">
                Demander
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal pour marquer une modification comme terminée */}
      <Modal
        title="Marquer la modification comme terminée"
        open={showCompleteModal}
        onCancel={() => {
          setShowCompleteModal(false);
          setSelectedModification(null);
          completeForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={completeForm} onFinish={handleCompleteModification} layout="vertical">
          <Form.Item
            name="date_completed"
            label="Date de completion"
            rules={[{ required: true, message: 'Veuillez sélectionner la date' }]}
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY"
              placeholder="Sélectionner la date de completion"
            />
          </Form.Item>
          <Form.Item
            name="observations"
            label="Observations finales (optionnel)"
          >
            <TextArea 
              rows={3} 
              placeholder="Commentaires sur les modifications effectuées..."
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setShowCompleteModal(false);
                setSelectedModification(null);
                completeForm.resetFields();
              }}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit">
                Terminer
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};